import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { User, Mail, Phone, Building2, FileText, MapPin, ArrowLeft, CreditCard, Trash2, Loader2, Plus } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BRAND_DISPLAY = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

const Profil = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [savedCards, setSavedCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (user && token) {
      fetchSavedCards();
    }
  }, [user, token]);

  const fetchSavedCards = async () => {
    setLoadingCards(true);
    try {
      const res = await axios.get(`${API}/user/payment-methods?token=${token}`);
      setSavedCards(res.data.payment_methods || []);
    } catch (err) {
      console.error('Error fetching cards:', err);
    }
    setLoadingCards(false);
  };

  const handleDeleteCard = async (pmId) => {
    if (!window.confirm('Supprimer cette carte ?')) return;
    setDeletingId(pmId);
    try {
      await axios.delete(`${API}/user/payment-methods/${pmId}?token=${token}`);
      setSavedCards(prev => prev.filter(c => c.id !== pmId));
    } catch (err) {
      alert('Erreur lors de la suppression');
    }
    setDeletingId(null);
  };

  if (!user) {
    navigate('/connexion');
    return null;
  }

  const isPro = user.type === 'professionnel';

  const fields = [
    { icon: User, label: 'Nom et prénom', value: user.name },
    ...(!isPro ? [{ icon: MapPin, label: 'Adresse', value: user.adresse }] : []),
    { icon: Mail, label: 'Email', value: user.email },
    { icon: Phone, label: 'Téléphone', value: user.phone },
    { icon: FileText, label: 'Type de compte', value: isPro ? 'Professionnel' : 'Particulier' },
    ...(isPro ? [
      { icon: Building2, label: 'Société', value: user.societe },
      { icon: FileText, label: 'SIREN', value: user.siren },
      { icon: MapPin, label: 'Adresse de la société', value: user.adresse_societe },
    ] : []),
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-10 px-4">
      <div className="max-w-xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/mon-compte')}
          className="mb-4 text-gray-600 hover:text-green-600"
          data-testid="back-to-compte"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>

        <Card className="p-6 mb-6" data-testid="profil-card">
          <div className="flex items-center gap-4 mb-6 pb-5 border-b border-gray-100">
            <div className="h-16 w-16 rounded-full bg-green-600 text-white font-bold text-xl flex items-center justify-center flex-shrink-0">
              {user.name ? user.name.split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase() : '?'}
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{user.name}</h1>
              <p className="text-sm text-gray-500">{isPro ? 'Compte professionnel' : 'Compte particulier'}</p>
            </div>
          </div>

          <div className="space-y-0">
            {fields.map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-center gap-4 py-3.5 border-b border-gray-50 last:border-0" data-testid={`profil-field-${label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="h-9 w-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Icon className="h-4 w-4 text-gray-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-400">{label}</p>
                  <p className="text-sm font-medium text-gray-900 truncate">{value || '—'}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Saved Cards Section */}
        <Card className="p-6" data-testid="saved-cards-management">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-green-600" />
              Mes cartes bancaires
            </h2>
          </div>

          {loadingCards ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-green-600" />
            </div>
          ) : savedCards.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Aucune carte enregistrée</p>
              <p className="text-xs text-gray-400 mt-1">
                Lors de votre prochain paiement, cochez "Garder la carte en mémoire" pour sauvegarder votre carte.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {savedCards.map((card) => (
                <div
                  key={card.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                  data-testid={`manage-card-${card.last4}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                      <CreditCard className="h-5 w-5 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {BRAND_DISPLAY[card.brand] || card.brand} •••• {card.last4}
                      </p>
                      <p className="text-xs text-gray-500">
                        Expire {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteCard(card.id)}
                    disabled={deletingId === card.id}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-card-${card.last4}`}
                  >
                    {deletingId === card.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Profil;
